import type { ListTaxonomyNodesResponse } from '@/api/generatedApi';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { AxiosApiError, getApiError } from '@/types/apiError';
import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import { TAXONOMY_CHILDREN_QUERY_KEY } from './useGetTaxonomyChildren';

export const useRenameTaxonomyNode = (taxonomyId: string) => {
  const queryClient = useQueryClient();
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();

  const { mutate, isPending, error } = useMutation({
    mutationFn: (data: { nodeId: string; name: string }) =>
      api.taxonomy.renameTaxonomyNode(tenantId, taxonomyId, data.nodeId, { name: data.name }),
    onMutate: async ({ nodeId, name }) => {
      const previousData = queryClient.getQueriesData({ queryKey: [TAXONOMY_CHILDREN_QUERY_KEY] });

      queryClient.setQueriesData<InfiniteData<AxiosResponse<ListTaxonomyNodesResponse>>>(
        { queryKey: [TAXONOMY_CHILDREN_QUERY_KEY] },
        old => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map(page => ({
              ...page,
              data: {
                ...page.data,
                nodes: page.data.nodes.map(node => (node.id === nodeId ? { ...node, name } : node)),
              },
            })),
          };
        }
      );

      return { previousData };
    },
    onError: (_error: AxiosApiError, _variables, context) => {
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [TAXONOMY_CHILDREN_QUERY_KEY],
        exact: false,
      });
    },
  });

  return {
    renameNode: mutate,
    isRenaming: isPending,
    renameError: getApiError(error),
  };
};
